// ============================================================================
// LE CATALOGUE DES DROITS D'OPÉRATEUR — ce qu'une caisse sait refuser, et à qui.
//
// POURQUOI CE FICHIER EXISTE, ET POURQUOI ICI. Le SPEC §26.5 a figé la STRUCTURE en juin 2026 (rôle
// de base + surcharge par compte, liste de rôles FOURNIE par l'éditeur, déblocage ponctuel au PIN
// d'un supérieur) en renvoyant le CATALOGUE détaillé « à la réalisation du back-office ». Le voici.
// Il vit dans le contrat parce que les DEUX bouts en dépendent : le back-office pour proposer et
// écrire, la caisse pour appliquer. Une règle que les deux doivent connaître EST le contrat
// (§24.3bis) ; dupliquée de chaque côté, elle diverge au premier droit ajouté — et celle qui perd
// accorde le droit que personne n'a donné.
//
// ⚠️ TROIS ÉTATS, PAS DEUX, et c'est le cœur du modèle métier. « Interdit » et « Libre » sont
// évidents ; « Code responsable » est celui qui fait vivre un magasin — la caisse accepte, mais après
// le PIN d'un responsable, NOMINATIF, et le journal retient qui a autorisé quoi (§26.5, JET §14.7).
// Un modèle à deux états obligerait à choisir entre bloquer un vendeur et tout lui ouvrir.
// ⚠️ LE MOT D'ÉCRAN A CHANGÉ, LA VALEUR NON : `'ask'` est persistée (`operator.rights_mode`) et
// voyage vers la flotte, donc elle se MIGRERAIT au lieu de se renommer (Forme J). « À valider »
// était juste mais ne disait ni par qui ni comment ; le terme nomme désormais le geste réel.
//
// ⚠️ LES CLÉS SONT CELLES QUI SONT DÉJÀ EN BASE, pas celles de la maquette. Le dessin nommait
// « sess » et « annt » deux droits que la caisse interroge depuis des mois sous `openClose` et
// `cancelSale` (`operator.rights`, lu par `hasRight`). Adopter les noms du dessin aurait écrit un
// droit que personne ne lit — un vendeur aurait perdu l'ouverture de session le jour du déploiement,
// sans qu'aucun test ne s'en aperçoive. Un nom persisté ne se renomme pas (Forme J) : c'est le
// catalogue qui prend celui de la base.
//
// ⚠️ LA LISTE EST EXHAUSTIVE (décision utilisateur) : tout droit que la caisse interroge y figure, avec sa valeur par
// modèle — les droits d'administration de la tablette compris (`settings.view`, `settings.write`, `audit`, `admin`).
// Un droit appliqué sans figurer ici ne se réglerait nulle part : seul le compte de l'éditeur le recevait.
//
// ⚠️ CE QUE LA CAISSE APPLIQUE AUJOURD'HUI EST UN SOUS-ENSEMBLE, et il est NOMMÉ ici
// (`REGISTER_ENFORCED`). Sans cette liste, le back-office afficherait trente-quatre interrupteurs
// dont deux agissent, et rien à l'écran ne dirait lesquels : une option offerte qui n'aboutit pas est
// le sixième interdit du §23.0. Le réglage n'est pas perdu pour autant — il est écrit, il DESCEND
// par le canal `operators`, et il attend que la caisse l'applique. C'est ce qu'il faut dire, ni plus
// (« ça marche ») ni moins (« ça ne sert à rien »).
// ⚠️ ELLE EST UNE MESURE, PAS UN VŒU : ce sont les clés réellement passées à `hasRight` dans le code
// de la caisse. Elle GRANDIT quand la caisse apprend à en appliquer une — jamais la veille, sinon
// l'écran se remet à mentir.
// ============================================================================

/** L'état d'un droit. ⚠️ `ask` = la caisse accepte APRÈS le PIN d'un responsable, jamais en silence. */
export type RightValue = 'no' | 'ask' | 'yes';

/**
 * Comment un droit se règle.
 * · `boolean`    — oui / non. Il n'a pas de sens intermédiaire (on encaisse, ou on n'encaisse pas).
 * · `tri`        — interdit / code responsable / libre.
 * · `backoffice` — il ne s'exerce PAS depuis la caisse (archives fiscales, RGPD, clôture
 *                  d'inventaire). ⚠️ Il figure au catalogue pour être VU, pas pour être coché :
 *                  son absence ferait chercher un droit manquant qui n'existe pas ici.
 */
export type RightMode = 'boolean' | 'tri' | 'backoffice';

/**
 * Les modèles de l'ÉDITEUR — les trois que chaque magasin reçoit, et la BASE de tout modèle qu'il crée
 * (`rights-templates.ts`). ⚠️ Un modèle du magasin s'exprime toujours par rapport à l'un d'eux : c'est ce qui laisse
 * les appareils résoudre les droits d'un compte avec ce seul catalogue.
 */
export type TemplateKey = 'vend' | 'resp' | 'ger';

/** Les plafonds de remise d'un modèle, en points de base (100 % = 10 000) : sans motif, puis avec motif. */
export interface DiscountCeilings {
  readonly freeBp: number;
  readonly maxBp: number;
}

export interface RightDef {
  readonly key: string;
  readonly label: string;
  /** Ce que le droit fait vraiment — la phrase qui évite de cocher au jugé. */
  readonly note: string;
  readonly mode: RightMode;
  /** Valeur posée par chaque modèle. Absente sur un droit `backoffice` : il ne se règle pas ici. */
  readonly defaults?: Readonly<Record<TemplateKey, RightValue>>;
}

export interface RightDomain {
  readonly key: string;
  readonly label: string;
  /** Le tracé SVG de l'icône du domaine — donnée du catalogue, pas un jeton de charte. */
  readonly icon: string;
  readonly rights: readonly RightDef[];
}

export const RIGHT_DOMAINS: readonly RightDomain[] = [
  {
    key: 'caisse',
    label: 'Caisse & encaissement',
    icon: 'M4.5 5h15a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM2.5 10h19M6 15h4',
    rights: [
      // ⚠️ `openClose` et non « sess » : la caisse interroge CE nom depuis des mois (voir en-tête).
      { key: 'openClose', label: 'Ouvrir une session de caisse', note: 'fond de caisse, ouverture de journée', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'sale', label: 'Encaisser une vente', note: 'le socle du métier — jamais refusé à un vendeur', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'draw', label: 'Ouvrir le tiroir hors vente', note: 'chaque ouverture est tracée', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'ec', label: 'Justifier un écart de fond de caisse', note: 'motif obligatoire, remonte au Centre d’écarts', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      { key: 'z', label: 'Clôturer la journée (Z)', note: 'irréversible, scellé NF525', mode: 'tri', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
      { key: 'dup', label: 'Réimprimer un ticket (duplicata)', note: 'marqué DUPLICATA, plafonné à 3 par ticket', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      // ⚠️ `cashMovement` est le nom que la SPEC §26.2 donne à ce droit depuis juin ; jamais persisté avant ce catalogue.
      { key: 'cashMovement', label: 'Faire un prélèvement', note: 'remise au coffre ou frais du magasin — l’argent sort du tiroir', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'payChange', label: 'Changer le règlement d’une vente encaissée', note: 'pièce corrective signée — motif obligatoire', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'heldOther', label: 'Reprendre la vente en attente d’un collègue', note: 'une vente mise en attente par un autre opérateur', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'tpeDecide', label: 'Trancher un paiement carte resté sans réponse', note: 'accepté ou refusé à la main, quand le terminal ne répond plus', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'arch', label: 'Exporter les archives fiscales', note: 'action de back-office — jamais depuis la caisse', mode: 'backoffice' },
    ],
  },
  {
    key: 'prix',
    label: 'Prix & remises',
    icon: 'M3 12V4h8l10 10-8 8L3 12zM8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
    rights: [
      { key: 'rem', label: 'Accorder une remise', note: 'les plafonds en % se règlent dans Référentiels → Remises', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'libre', label: 'Saisir un prix libre', note: 'sort du barème — tracé comme un écart de prix', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      { key: 'annl', label: 'Annuler une ligne avant encaissement', note: 'panier en cours, rien n’est encore signé', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      // ⚠️ `cancelSale` et non « annt » : même raison qu'`openClose`.
      { key: 'cancelSale', label: 'Annuler un ticket complet', note: 'après signature : génère une pièce d’annulation', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
    ],
  },
  {
    key: 'ret',
    label: 'Retours & avoirs',
    icon: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5',
    rights: [
      { key: 'reprise', label: 'Reprendre un article', note: 'retour en stock, valorisé au PMP', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'avoir', label: 'Émettre un avoir', note: 'titre de paiement — dette envers le porteur', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'cash', label: 'Rembourser en espèces', note: 'sort de l’argent du tiroir', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
    ],
  },
  {
    key: 'tit',
    label: 'Titres & bons',
    icon: 'M5 3h14v18l-2-2-2 2-2-2-2 2-2-2-2 2-2-2V3zM9 8h6M9 12h6M9 16h4',
    rights: [
      { key: 'porteur', label: 'Accepter un titre au porteur', note: 'sans identification du client — comme un billet', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'kd', label: 'Émettre un chèque cadeau', note: 'encaissement immédiat, consommation différée', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'acompte', label: 'Vendre un acompte', note: 'encaissé maintenant, déduit d’un achat futur du client', mode: 'tri', defaults: { vend: 'ask', resp: 'yes', ger: 'yes' } },
      { key: 'force', label: 'Forcer un titre expiré', note: 'geste commercial — motif obligatoire', mode: 'tri', defaults: { vend: 'no', resp: 'no', ger: 'yes' } },
      // ⚠️ `passePartout`, la clé que la caisse interroge depuis la reprise des avoirs (§7.2) : un montant LIBRE, sans
      //    pièce d'origine — de l'argent créé au comptoir, d'où « non » par défaut sous la gérante.
      { key: 'passePartout', label: 'Reprendre un avoir de l’ancien logiciel', note: 'montant libre, sans pièce d’origine — motif obligatoire, tracé au journal', mode: 'boolean', defaults: { vend: 'no', resp: 'no', ger: 'yes' } },
    ],
  },
  {
    key: 'cli',
    label: 'Clients & fidélité',
    icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
    rights: [
      { key: 'clicr', label: 'Créer ou modifier une fiche', note: 'coordonnées, opt-in, wallet', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      // ⚠️ UN DROIT À PART, et pas une partie de `clicr` : sans lui, tout vendeur habilité à corriger une
      //    adresse pourrait accorder 100 % à vie à n'importe qui (CADRAGE-Remise-Permanente.md, R12).
      { key: 'remcli', label: 'Accorder une remise permanente à un client', note: 'le taux en % de la fiche, appliqué d’office à chaque vente', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
      { key: 'clivue', label: 'Voir les coordonnées complètes', note: 'sinon, seuls le nom et la cagnotte s’affichent', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'cag', label: 'Ajuster la cagnotte à la main', note: 'écrit au grand livre de fidélité', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      // ⚠️ DEUX MODULES DE LA CAISSE, ouverts aux trois modèles : ils servent le client au comptoir. L'échéancier montre
      //    des montants dus par des clients nommés, d'où un droit qu'on peut fermer compte par compte.
      { key: 'alteration', label: 'Ouvrir les retouches', note: 'le suivi des retouches confiées par les clients', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'reminders', label: 'Ouvrir l’échéancier', note: 'les montants dus par des clients nommés', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'anon', label: 'Anonymiser une fiche (RGPD)', note: 'irréversible — action de back-office', mode: 'backoffice' },
    ],
  },
  {
    key: 'cat',
    label: 'Catalogue',
    icon: 'M4 6h16M4 12h16M4 18h10',
    rights: [
      { key: 'artq', label: 'Créer un article à la volée en caisse', note: 'ne bloque jamais une vente — part dans À valider', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'artv', label: 'Valider un article créé en caisse', note: 'entrée au catalogue — action de back-office', mode: 'backoffice' },
      { key: 'lab', label: 'Imprimer des étiquettes', note: 'rouleau ou planche A4', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
    ],
  },
  {
    key: 'stk',
    label: 'Stock & Mobile Partner',
    icon: 'M3 7l9-4 9 4v10l-9 4-9-4V7zM3 7l9 4 9-4M12 11v10',
    rights: [
      { key: 'scan', label: 'Se connecter à Mobile Partner', note: 'sans ce droit, l’appareil refuse le compte', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
      { key: 'stv', label: 'Consulter les stocks, local et distant', note: 'lecture seule, y compris les autres magasins', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      { key: 'invz', label: 'Compter une zone d’inventaire', note: 'scan de comptage, poussé au back-office', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
      { key: 'invc', label: 'Clôturer un inventaire', note: 'écrit les mouvements — action de back-office', mode: 'backoffice' },
      { key: 'bl', label: 'Contrôler une réception (BL)', note: 'scan du réel avant entrée en stock', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
      { key: 'bld', label: 'Créer un BL direct par scan', note: 'livraison sans commande en face', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      { key: 'cor', label: 'Corriger le stock à la main', note: 'crée un mouvement signé, motif obligatoire', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      { key: 'rex', label: 'Expédier une rétrocession', note: 'sortie de stock vers un autre magasin', mode: 'tri', defaults: { vend: 'no', resp: 'ask', ger: 'yes' } },
      { key: 'rre', label: 'Réceptionner une rétrocession', note: 'contrôle à l’arrivée puis entrée en stock', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
    ],
  },
  {
    key: 'moi',
    label: 'Chiffres & ventes',
    icon: 'M4 19V9M10 19V5M16 19v-7M22 19H2',
    rights: [
      { key: 'moi', label: 'Voir ses ventes et son CA sur la tablette', note: 'ses chiffres à lui seul — jamais ceux des collègues', mode: 'boolean', defaults: { vend: 'yes', resp: 'yes', ger: 'yes' } },
      // ⚠️ FERMÉ AU VENDEUR (décision utilisateur) : sans lui, chacun ne voit que ses ventes et son CA.
      { key: 'team', label: 'Voir les ventes et les chiffres des collègues', note: 'journal et historique du magasin, classement des vendeurs', mode: 'boolean', defaults: { vend: 'no', resp: 'yes', ger: 'yes' } },
    ],
  },
  {
    // ⚠️ LES CLÉS SONT CELLES QUE LA CAISSE INTERROGE DEPUIS TOUJOURS (`settings.view`, `settings.write`, `audit`,
    //    `admin`, lues par `hasRight` dans `App.tsx`) — même raison qu'`openClose` : un nom persisté ne se renomme pas.
    // ⚠️ FERMÉS PAR DÉFAUT AU RESPONSABLE (décision utilisateur) : les réglages engagent tout le magasin.
    key: 'regl',
    label: 'Réglages de la caisse',
    icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
    rights: [
      { key: 'settings.view', label: 'Voir les réglages de la caisse', note: 'en lecture seule — sans ce droit, l’écran Réglages est refusé', mode: 'boolean', defaults: { vend: 'no', resp: 'no', ger: 'yes' } },
      { key: 'settings.write', label: 'Modifier les réglages de la caisse', note: 'ouvre l’écran en édition — sans lui, le droit de voir le montre en lecture seule', mode: 'boolean', defaults: { vend: 'no', resp: 'no', ger: 'yes' } },
      { key: 'audit', label: 'Ouvrir l’Inspecteur NF525', note: 'les pièces signées et le journal — chaque consultation est tracée', mode: 'boolean', defaults: { vend: 'no', resp: 'no', ger: 'yes' } },
      // ⚠️ FERMÉ AUX TROIS MODÈLES, OUVRABLE COMPTE PAR COMPTE : il change l'adresse du serveur et répare la base — une
      //    erreur y coupe la caisse du siège. Le compte de l'éditeur le reçoit de la Console.
      { key: 'admin', label: 'Outils techniques de la caisse', note: 'adresse du serveur, réparation de la base — une erreur coupe la caisse du siège', mode: 'boolean', defaults: { vend: 'no', resp: 'no', ger: 'no' } },
    ],
  },
];

/** Les modèles, dans l'ordre d'affichage — du moins au plus habilité. */
export const RIGHT_TEMPLATES: readonly { key: TemplateKey; label: string; icon: string }[] = [
  { key: 'vend', label: 'Vendeur', icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0' },
  { key: 'resp', label: 'Responsable', icon: 'M12 3l2.6 5.5 6 .9-4.3 4.2 1 6L12 16.8 6.7 19.6l1-6L3.4 9.4l6-.9z' },
  { key: 'ger', label: 'Gérante', icon: 'M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V7a4 4 0 0 1 8 0v4' },
];

/**
 * Le RÔLE d'un compte, lu depuis `operator.rights_mode`.
 *
 * ⚠️ IL VIT ICI PARCE QUE DEUX BOUTS EN DÉPENDENT : le back-office l'écrit, les terminaux l'affichent.
 * Recopier la correspondance côté device la ferait diverger au premier modèle ajouté — et celui qui
 * perd afficherait un rôle qui n'existe pas.
 *
 * ⚠️ `null` = AUCUN RÔLE, et ce n'est pas « vendeur par défaut ». Un compte sans modèle est ANTÉRIEUR
 * au catalogue : aucun droit ne se résout pour lui, et le back-office le signale déjà en ambre. Lui
 * prêter le rôle le plus faible ferait croire à une habilitation que personne n'a donnée.
 *
 * ⚠️ UN CONTENU ILLISIBLE VAUT « AUCUN RÔLE », jamais une exception : cette valeur vient d'une base
 * distante et traverse un journal de synchronisation ; elle ne doit pas pouvoir faire tomber un écran
 * de connexion.
 */
export function roleLabelOf(rightsMode: string | null | undefined): string | null {
  // ⚠️ ON RÉUTILISE LE LECTEUR DU CONTRAT plutôt que de refaire son analyse : lui seul sait ce qui
  //    est un modèle valide, et une seconde lecture accepterait des clés que celle-ci refuse.
  const mode = parseRightsMode(rightsMode);
  if (mode === null) return null;
  // ⚠️ LE NOM DU MODÈLE DU MAGASIN D'ABORD : un modèle renommé ou créé en magasin (« Vendeur saisonnier ») se montre
  //    sous son nom, et seul un compte d'avant retombe sur celui du modèle de l'éditeur.
  return mode.templateLabel ?? RIGHT_TEMPLATES.find((t) => t.key === mode.template)?.label ?? null;
}
/** Le résumé de chaque modèle, tel qu'il se lit sur sa carte — une phrase, pas une liste de droits. */
export const TEMPLATE_SUMMARY: Readonly<Record<TemplateKey, string>> = {
  vend: 'Vend, encaisse, reprend un article ; ne voit que ses propres ventes. Remise, duplicata et prélèvement passent par un responsable ; ni Z, ni correction de stock.',
  resp: 'Tout le comptoir sans demander : remise, retour, avoir, Z. Écarts de caisse et corrections de stock restent sous code responsable ; les réglages de la caisse lui sont fermés.',
  ger: 'Rien ne lui est fermé côté caisse, stock et réglages. Les outils techniques s’ouvrent compte par compte ; les actions de back-office (archives, RGPD, clôture d’inventaire) restent ailleurs.',
};

/** Tous les droits à plat, dans l'ordre du catalogue — l'itération que tout le monde refait sinon. */
export const ALL_RIGHTS: readonly RightDef[] = RIGHT_DOMAINS.flatMap((d) => d.rights);

const PAR_CLE = new Map(ALL_RIGHTS.map((r) => [r.key, r]));

/** Le droit, ou `null` s'il n'est pas au catalogue — un nom inconnu n'est jamais accordé. */
export function rightDef(key: string): RightDef | null {
  return PAR_CLE.get(key) ?? null;
}

/**
 * LES DROITS QUE LA CAISSE APPLIQUE VRAIMENT AUJOURD'HUI.
 *
 * ⚠️ MESURÉE SUR LE CODE DE LA CAISSE, pas déclarée : ce sont les clés du catalogue réellement
 * passées à `hasRight` dans `apps/sliderz/retail`. Le back-office s'en sert pour DIRE, droit par
 * droit, si le réglage agit déjà ou s'il attend la caisse.
 * ⚠️ ELLE NE S'ÉLARGIT QU'APRÈS COUP — le jour où la caisse interroge `draw`, on l'ajoute ; pas la
 * veille. Une liste optimiste est exactement le mensonge qu'elle est censée empêcher.
 * ⚠️ 🔴 ET ELLE L'A ÉTÉ : `cancelSale` y a figuré alors qu'AUCUN écran de la caisse ne l'interrogeait —
 * le bouton d'annulation du journal ouvrait sa modale sans contrôler le moindre droit. Le siège
 * annonçait deux droits appliqués là où il n'y en avait qu'un, sur une opération FISCALE
 * (contre-passation signée, JET 190). Il avait été RETIRÉ pour que l'écran dise vrai, l'arbitrage
 * étant à part — il restreint une opération jusque-là ouverte à tout opérateur connecté.
 * ✅ **L'ARBITRAGE EST TRANCHÉ** : la caisse l'applique désormais, à l'écran ET dans le
 * moteur (`cancel-sale.ts`), et il revient donc dans cette liste. ⚠️ **Le calendrier est le point
 * délicat, et il se règle tout seul** : l'application part dans un APK, qui met du temps à atteindre
 * la flotte — le siège a cette fenêtre pour accorder le droit. Mesuré sur la bêta avant de décider :
 * 7 opérateurs actifs sur un magasin, **un seul** portait `cancelSale`.
 * ⚠️ LA LISTE EST DÉSORMAIS MESURÉE PAR UNE GARDE, pas seulement par la bonne volonté :
 * `apps/sliderz/retail/__tests__/droits-appliques.test.ts` lit les appels `hasRight` de la caisse et
 * refuse tout écart, dans les deux sens.
 */
export const REGISTER_ENFORCED: readonly string[] = ['openClose', 'cancelSale', 'clicr', 'remcli', 'cag', 'passePartout', 'settings.view', 'settings.write', 'audit', 'admin', 'moi', 'stv', 'alteration', 'reminders'];

/**
 * LE DROIT QUI OUVRE SLIDERZ MOBILE PARTNER — et sans lequel l'appareil refuse le compte.
 *
 * ⚠️ 🔴 IL ÉTAIT ÉCRIT DANS CE CATALOGUE ET APPLIQUÉ NULLE PART (constaté sur terminal le
 * 2026-09-09). La note du droit `scan` dit mot pour mot « sans ce droit, l'appareil refuse le
 * compte » ; en pratique TOUS les opérateurs actifs du magasin descendaient et s'affichaient à
 * l'écran de connexion, y compris ceux que le siège n'a jamais habilités. Le back-office les compte
 * pourtant déjà (« Habilités Mobile Partner »), donc les deux moitiés existaient — elles ne se
 * parlaient pas.
 *
 * ⚠️ CE N'EST PAS QU'UNE QUESTION D'ENCOMBREMENT D'ÉCRAN : c'est l'EMPREINTE DE PIN de tout le
 * personnel qui descendait sur un terminal de poche. Le filtrage doit donc vivre côté SERVEUR, à la
 * source ; masquer à l'écran ne retire rien de la base locale.
 */
export const MOBILE_PARTNER_LOGIN_RIGHT = 'scan';

/**
 * CE QU'UNE TUILE DE L'ACCUEIL DEMANDE À L'OPÉRATEUR — la correspondance, écrite UNE fois.
 *
 * ⚠️ IL EXISTE DEUX VOCABULAIRES DE DROITS, ET ILS SONT TOUS DEUX PERSISTÉS. Les confondre est
 * exactement ce qui a fermé les cinq tuiles d'un terminal réel :
 *   · L'APPAREIL (`device_config.rights`, posé à la Console) parle en `mobile-partner.inventaire`,
 *     `.reception`, `.transfert`, `.consultation`… Il dit ce que CETTE MACHINE est configurée — et
 *     facturée — pour faire.
 *   · L'OPÉRATEUR (`operator.rights`, réglé au back-office) parle les clés COURTES de ce catalogue :
 *     `scan`, `invz`, `bl`, `stv`, `rex`… Il dit ce que CETTE PERSONNE a le droit de faire.
 * L'application intersecte les deux ; sans cette table, la moitié OPÉRATEUR ne pouvait jamais
 * s'exprimer, et un commentaire affirmait même que « le back-office ne transmet pas encore de droits
 * par opérateur » — c'était faux, il les transmet sous d'autres noms.
 *
 * ⚠️ `null` VEUT DIRE « OUVERT À QUICONQUE PEUT SE CONNECTER », jamais « oubli ». La consultation de
 * fidélité n'a pas de droit dédié : en caisse, le nom et la cagnotte s'affichent pour tout le monde
 * (`clivue` ne gouverne que les coordonnées complètes). Lui inventer une clé accorderait ou
 * refuserait un droit que personne n'a défini.
 */
export const MOBILE_PARTNER_TILE_RIGHTS: Readonly<Record<string, string | null>> = {
  inventory: 'invz',
  reception: 'bl',
  transfer: 'rex',
  stock: 'stv',
  loyalty: null,
};

/**
 * ⚠️ 🔴 `MOBILE_PARTNER_TILE_DEVICE_RIGHTS` A ÉTÉ RETIRÉ LE 2026-09-10, et il faut dire pourquoi
 * pour que personne ne le réécrive. Il portait la seconde moitié d'un CROISEMENT appareil ×
 * opérateur que la spec interdit explicitement (§5 : « Gating = droits opérateur UNIQUEMENT",
 * décision 2026-07 […] pas de double filtre appareil × opérateur »). Son commentaire affirmait
 * l'inverse de la décision produit, avec un argument plausible — et il a fermé les cinq tuiles
 * d'un terminal dont l'opératrice portait TOUS les droits.
 *
 * ⚠️ LES CODES `mobile-partner.*` EXISTENT TOUJOURS À LA CONSOLE, et c'est légitime : ils disent ce
 * pour quoi une machine est configurée et facturée. Ils ne gouvernent simplement AUCUN écran.
 */

/**
 * LES DROITS « CODE RESPONSABLE » QUE LA CAISSE SAIT DEMANDER — le geste reste offert, le code d'un responsable
 * l'ouvre, et le JET 130 retient qui a autorisé quoi (`docs/CADRAGE-Droits-Caisse.md` §3).
 * ⚠️ MESURÉE comme `REGISTER_ENFORCED`, jamais déclarée : ce sont les clés que la caisse passe à `ensureRight`
 *    (`droits-appliques.test.ts`). Hors de cette liste, « Code responsable » se comporte encore comme « Interdit ».
 */
export const REGISTER_ASKS: readonly string[] = ['cancelSale', 'cag'];

/**
 * LES MODÈLES QUI PEUVENT TAPER LEUR CODE pour autoriser un geste « Code responsable » — décision utilisateur :
 * tout compte Responsable ou Gérante, quel que soit le droit concerné. Un compte sans modèle (jamais
 * réglé au siège) n'autorise rien.
 */
export const APPROVER_TEMPLATES: readonly TemplateKey[] = ['resp', 'ger'];

/** Ce compte (son `rights_mode`) peut-il autoriser un geste « Code responsable » ? */
export function canApprove(rightsMode: string | null | undefined): boolean {
  const mode = parseRightsMode(rightsMode);
  return mode !== null && APPROVER_TEMPLATES.includes(mode.template);
}

/** La caisse sait-elle DÉJÀ appliquer ce droit ? (sinon : réglé et descendu, en attente). */
export function isEnforcedByRegister(key: string): boolean {
  return REGISTER_ENFORCED.includes(key);
}

/**
 * La caisse REFUSE-t-elle ce réglage, alors même qu'elle applique le droit ?
 *
 * ⚠️ LE PIÈGE QUE CETTE FONCTION EXISTE POUR DIRE : aucun écran de caisse ne sait demander un code
 * responsable aujourd'hui, donc `mergeStoredRights` fait descendre `ask` en `false`. Sur un droit que
 * la caisse applique VRAIMENT, « Code responsable » se comporte donc comme « Interdit » — plus
 * restrictif que voulu, jamais plus permissif, mais le gérant croit avoir ouvert le droit.
 * ⚠️ ELLE SE DÉDUIT des deux faits, elle ne les recopie pas : le jour où la caisse saura demander,
 * c'est `mergeStoredRights` qui changera, et cette règle avec lui.
 */
export function isRefusedByRegister(key: string, value: RightValue): boolean {
  return value === 'ask' && isEnforcedByRegister(key) && !REGISTER_ASKS.includes(key);
}

/** La valeur qu'un modèle pose pour un droit. `null` si le droit ne se règle pas en caisse. */
export function templateValue(template: TemplateKey, rightKey: string): RightValue | null {
  return rightDef(rightKey)?.defaults?.[template] ?? null;
}

/**
 * LA VALEUR EFFECTIVE d'un droit pour un compte : sa surcharge si elle existe, sinon son modèle.
 * ⚠️ UNE SEULE FONCTION POUR LES DEUX BOUTS — l'écran qui l'affiche et la caisse qui l'applique
 * doivent lire la même règle, sinon un droit s'affiche « Libre » et se refuse au comptoir.
 */
export function effectiveRight(
  template: TemplateKey,
  rightKey: string,
  overrides: Readonly<Record<string, RightValue>> | null | undefined,
): RightValue | null {
  return overrides?.[rightKey] ?? templateValue(template, rightKey);
}

/**
 * Le jeu COMPLET des valeurs effectives d'un compte — modèle + surcharges, tous droits réglables.
 * Les droits `backoffice` en sont exclus : ils ne s'exercent pas depuis la caisse.
 */
export function resolveRights(
  template: TemplateKey,
  overrides: Readonly<Record<string, RightValue>> | null | undefined,
): Record<string, RightValue> {
  const out: Record<string, RightValue> = {};
  for (const r of ALL_RIGHTS) {
    if (r.mode === 'backoffice') continue;
    const v = effectiveRight(template, r.key, overrides);
    if (v) out[r.key] = v;
  }
  return out;
}

/**
 * Les surcharges NETTOYÉES : une valeur identique à celle du modèle n'est pas une surcharge.
 * ⚠️ Sans ce nettoyage, un aller-retour sur un interrupteur laisserait un compte marqué « surchargé »
 * alors qu'il est parfaitement aligné — et le repère de l'écran mentirait sur la dette de réglage.
 */
export function pruneOverrides(
  template: TemplateKey,
  overrides: Readonly<Record<string, RightValue>>,
): Record<string, RightValue> {
  const out: Record<string, RightValue> = {};
  for (const [key, v] of Object.entries(overrides)) {
    const def = rightDef(key);
    if (!def || def.mode === 'backoffice') continue;
    if (templateValue(template, key) !== v) out[key] = v;
  }
  return out;
}

/**
 * LES DROITS D'UN COMPTE QUE LE SIÈGE N'A JAMAIS RÉGLÉ (`rights_mode` absent), tels que la caisse les lit.
 *
 * ⚠️ POUR UN TEL COMPTE, UNE CLÉ ABSENTE VEUT DIRE « JAMAIS DÉCIDÉ », PAS « REFUSÉ ». La caisse apprend à
 *    appliquer les droits du catalogue au fil des livraisons (`REGISTER_ENFORCED`) ; un compte créé avant
 *    qu'un droit soit appliqué ne le porte pas, et sans cette règle il le perdrait le jour où la caisse
 *    commence à l'interroger — un vendeur ne pourrait plus créer une fiche après une mise à jour, sans que
 *    personne l'ait décidé.
 * ⚠️ LA RÈGLE : un droit que les TROIS modèles ouvrent est ouvert ; tout autre reste fermé. On ne devine
 *    pas le modèle d'un compte — un droit que les modèles ne s'accordent pas à ouvrir (la remise
 *    permanente : « non » pour le vendeur) n'est jamais accordé par défaut.
 * ⚠️ UN COMPTE RÉGLÉ AU SIÈGE N'Y PASSE PAS : sa projection (`mergeStoredRights`) fait foi, et une clé
 *    absente y est un refus délibéré. Une clé déjà présente n'est jamais réécrite, dans aucun sens.
 */
export function undecidedAccountRights(stored: Readonly<Record<string, boolean>>): Record<string, boolean> {
  const out: Record<string, boolean> = { ...stored };
  for (const def of ALL_RIGHTS) {
    if (def.mode === 'backoffice' || def.key in out) continue;
    if ((['vend', 'resp', 'ger'] as const).every((tpl) => templateValue(tpl, def.key) === 'yes')) out[def.key] = true;
  }
  return out;
}

/**
 * LA PROJECTION BOOLÉENNE que la caisse lit (`operator.rights`), fusionnée avec ce qui s'y trouvait.
 *
 * ⚠️ `ask` DEVIENT `false`, ET C'EST LE SEUL CHOIX SÛR. `hasRight` compare à `true` : rendre `ask`
 * vrai ouvrirait le droit SANS demander le PIN du responsable — l'inverse exact de ce que le gérant a
 * réglé, et sans que rien ne le montre. Tant que la caisse ne sait pas demander, « code responsable » se
 * comporte comme « interdit » : plus restrictif que voulu, jamais plus permissif.
 * ⚠️ ON NE TOUCHE PAS AUX CLÉS HORS CATALOGUE, et c'est une garde, pas une politesse : une clé qu'aucun
 * domaine ne déclare (un droit posé par une autre application) ne se gouverne pas ici, et l'écraser
 * serait une perte silencieuse, découverte au comptoir.
 * ⚠️ LA TRI-VALEUR N'EST PAS PERDUE : elle est rangée à part (`operator.rights_mode`), et c'est ELLE la
 * source. Cette projection en est DÉRIVÉE, ici et nulle part ailleurs — deux dérivations
 * divergeraient, et celle qui perd accorde un droit que personne n'a donné (Forme A).
 */
export function mergeStoredRights(
  values: Readonly<Record<string, RightValue>>,
  previous: Readonly<Record<string, boolean>> | null | undefined,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, on] of Object.entries(previous ?? {})) {
    if (!rightDef(key)) out[key] = on; // hors catalogue : conservé tel quel
  }
  for (const [key, v] of Object.entries(values)) if (v === 'yes') out[key] = true;
  return out;
}

/**
 * Combien de droits ces valeurs laissent libres, mettent sous code responsable, interdisent — les trois chiffres de la
 * carte d'un modèle, ou ceux d'un domaine quand `rights` est la liste de ce domaine (la fenêtre du modèle les montre
 * domaine par domaine). Un droit sans valeur compte comme interdit, comme en caisse.
 */
export function rightsTally(
  values: Readonly<Record<string, RightValue>>, rights: readonly RightDef[] = ALL_RIGHTS,
): { yes: number; ask: number; no: number } {
  let yes = 0, ask = 0, no = 0;
  for (const r of rights) {
    if (r.mode === 'backoffice') continue;
    const v = values[r.key];
    if (v === 'yes') yes++; else if (v === 'ask') ask++; else no++;
  }
  return { yes, ask, no };
}

/**
 * CE QUE PORTE `operator.rights_mode` — les droits du compte, tels que tout appareil les applique, et le modèle du
 * magasin qu'il suit.
 * ⚠️ `template` + `overrides` SUFFISENT À UN APPAREIL : ce sont les droits EFFECTIFS exprimés par rapport au modèle de
 *    l'ÉDITEUR dont part le modèle du compte (le modèle du magasin et les exceptions du compte réunis). Une caisse ou un
 *    Mobile Partner installé avant les modèles du magasin les applique donc sans rien connaître d'autre
 *    (`docs/CADRAGE-Modeles-Droits-Magasin.md`, D3).
 * ⚠️ LE RESTE EST FACULTATIF, et absent d'un compte réglé avant les modèles du magasin : le modèle était alors celui de
 *    l'éditeur, et ses exceptions étaient `overrides` (`accountTemplateId`, `accountExceptions`).
 */
export interface OperatorRightsMode {
  template: TemplateKey;
  overrides: Record<string, RightValue>;
  /** Le modèle du MAGASIN que le compte suit — un des trois de l'éditeur, ou un modèle créé par le magasin. */
  storeTemplate?: string;
  /** Ce qui, sur ce compte, s'écarte du modèle du magasin. */
  exceptions?: Record<string, RightValue>;
  /** Le nom du modèle du magasin, pour qu'un appareil le montre. */
  templateLabel?: string;
  /** Les plafonds de remise du modèle du magasin. */
  discount?: DiscountCeilings;
}

/** Le modèle du magasin que suit un compte — celui de l'éditeur pour un compte réglé avant les modèles du magasin. */
export function accountTemplateId(mode: OperatorRightsMode): string {
  return mode.storeTemplate ?? mode.template;
}

/** Les exceptions d'un compte à son modèle du magasin — ses `overrides` pour un compte d'avant. */
export function accountExceptions(mode: OperatorRightsMode): Record<string, RightValue> {
  return mode.exceptions ?? mode.overrides;
}

/** Les valeurs dans l'ordre du catalogue — l'ordre où on les a posées ne doit pas changer la chaîne rangée. */
function inCatalogOrder(values: Readonly<Record<string, RightValue>>): Record<string, RightValue> {
  const out: Record<string, RightValue> = {};
  for (const r of ALL_RIGHTS) if (r.key in values) out[r.key] = values[r.key]!;
  return out;
}

/** Un identifiant de modèle du magasin lisible : court, sans espace (`vend`, `tpl-…`). */
const STORE_TEMPLATE_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** Une valeur en points de base acceptable pour un plafond : un entier de 0 à 10 000. */
function isCeilingBp(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10_000;
}

/** Les valeurs de droit connues d'un objet venu du réseau — une clé inconnue ou une valeur fausse est écartée. */
function readRightValues(raw: unknown): Record<string, RightValue> {
  const out: Record<string, RightValue> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (rightDef(k) && isRightValue(v)) out[k] = v;
  }
  return out;
}

/**
 * Relit `operator.rights_mode`. ⚠️ `null` sur tout ce qui n'est pas exploitable — colonne absente
 * (compte antérieur), JSON cassé, modèle inconnu. On ne DEVINE jamais un modèle : afficher
 * « Vendeur » par défaut ferait croire à un choix, et le premier enregistrement graverait ce choix
 * inventé. L'appelant doit dire « modèle inconnu » plutôt que d'en inventer un (§23.0).
 */
export function parseRightsMode(raw: string | null | undefined): OperatorRightsMode | null {
  if (!raw) return null;
  let lu: unknown;
  try { lu = JSON.parse(raw); } catch { return null; }
  if (!lu || typeof lu !== 'object') return null;
  const o = lu as { template?: unknown; overrides?: unknown; storeTemplate?: unknown; exceptions?: unknown; templateLabel?: unknown; discount?: unknown };
  if (!isTemplateKey(o.template)) return null;
  const mode: OperatorRightsMode = { template: o.template, overrides: readRightValues(o.overrides) };
  // ⚠️ UN CHAMP FACULTATIF ILLISIBLE EST IGNORÉ, jamais fatal : les droits effectifs (`template` + `overrides`) restent
  //    lisibles, et ce sont eux qui ouvrent ou ferment un geste.
  if (typeof o.storeTemplate === 'string' && STORE_TEMPLATE_ID_RE.test(o.storeTemplate)) {
    mode.storeTemplate = o.storeTemplate;
    mode.exceptions = readRightValues(o.exceptions);
  }
  if (typeof o.templateLabel === 'string' && o.templateLabel.trim()) mode.templateLabel = o.templateLabel.trim();
  const d = o.discount as { freeBp?: unknown; maxBp?: unknown } | null | undefined;
  if (d && typeof d === 'object' && isCeilingBp(d.freeBp) && isCeilingBp(d.maxBp) && d.freeBp <= d.maxBp) {
    mode.discount = { freeBp: d.freeBp, maxBp: d.maxBp };
  }
  return mode;
}

/** Les droits d'un compte tels qu'un appareil les applique, et d'où ils viennent. */
export interface ResolvedOperatorRights {
  /** La carte booléenne lue par `hasRight` — seul `true` ouvre. */
  rights: Record<string, boolean>;
  /** Les droits que le code d'un responsable ouvre geste par geste (`ask` au modèle). */
  approvalRights: string[];
  /** Le modèle du compte, `null` pour un compte jamais réglé ou au modèle illisible. */
  template: TemplateKey | null;
  /** Le modèle est présent mais illisible : la projection stockée s'applique telle quelle — l'appelant le DIT. */
  modeUnreadable: boolean;
}

/**
 * LES DROITS EFFECTIFS D'UN COMPTE sur un appareil — depuis son modèle et ses exceptions quand le siège l'a réglé,
 * depuis la projection stockée sinon.
 *
 * ⚠️ 🔴 UNE SEULE RÈGLE POUR LA CAISSE ET MOBILE PARTNER : Mobile Partner relisait la seule projection stockée
 *    (`operator.rights`), donc un droit entré au catalogue après le dernier enregistrement du compte lui manquait — le
 *    défaut que la caisse avait déjà corrigé. Deux lectures du même compte finissent par accorder deux choses différentes.
 * ⚠️ LES TROIS CAS : compte RÉGLÉ (`rights_mode` lisible) → modèle + exceptions, projection fusionnée (`mergeStoredRights`,
 *    clés hors catalogue gardées) · compte JAMAIS RÉGLÉ (`rights_mode` absent) → `undecidedAccountRights` · modèle
 *    ILLISIBLE → la projection telle quelle, et `modeUnreadable` pour que l'appelant le trace : on ne devine pas un modèle.
 */
export function resolveOperatorRights(
  stored: Readonly<Record<string, boolean>>,
  rightsModeRaw: string | null | undefined,
): ResolvedOperatorRights {
  const mode = parseRightsMode(rightsModeRaw);
  if (mode) {
    const values = resolveRights(mode.template, mode.overrides);
    return {
      rights: mergeStoredRights(values, stored),
      approvalRights: Object.keys(values).filter((k) => values[k] === 'ask'),
      template: mode.template,
      modeUnreadable: false,
    };
  }
  if (!rightsModeRaw) return { rights: undecidedAccountRights(stored), approvalRights: [], template: null, modeUnreadable: false };
  return { rights: { ...stored }, approvalRights: [], template: null, modeUnreadable: true };
}

/**
 * Sérialise pour `operator.rights_mode` — surcharges NETTOYÉES, pour ne pas graver du bruit.
 * ⚠️ L'ORDRE DES CLÉS EST FIXE : deux sérialisations du même état rendent la même chaîne, ce qui laisse comparer un
 *    compte à sa projection recalculée sans le réécrire pour rien.
 */
export function serializeRightsMode(mode: OperatorRightsMode): string {
  const out: Record<string, unknown> = { template: mode.template, overrides: inCatalogOrder(pruneOverrides(mode.template, mode.overrides)) };
  if (mode.storeTemplate !== undefined) {
    out.storeTemplate = mode.storeTemplate;
    out.exceptions = inCatalogOrder(mode.exceptions ?? {});
  }
  if (mode.templateLabel !== undefined) out.templateLabel = mode.templateLabel;
  if (mode.discount !== undefined) out.discount = { freeBp: mode.discount.freeBp, maxBp: mode.discount.maxBp };
  return JSON.stringify(out);
}

/** Une valeur venue du réseau est-elle un état de droit connu ? (rien d'autre ne s'écrit). */
export function isRightValue(v: unknown): v is RightValue {
  return v === 'no' || v === 'ask' || v === 'yes';
}

/** Un identifiant venu du réseau désigne-t-il un modèle de l'ÉDITEUR ? (les modèles du magasin en partent tous). */
export function isTemplateKey(v: unknown): v is TemplateKey {
  return v === 'vend' || v === 'resp' || v === 'ger';
}
